import React, { useCallback } from 'react';
import type { SeatSlot } from '@/types/seating-v2.types';

interface SeatChipProps {
  seat: SeatSlot;
  radius: number;
  isSelected: boolean;
  onSeatClick: (globalSeatNumber: number) => void;
  onSeatDrop: (globalSeatNumber: number, guestId: number) => void;
}

/**
 * Individual seat — a circular chip that acts as a drop target.
 * Shows guest initial when assigned, empty when not.
 */
export const SeatChip: React.FC<SeatChipProps> = ({
  seat,
  radius,
  isSelected,
  onSeatClick,
  onSeatDrop,
}) => {
  const isAssigned = !!seat.assignedGuestId;
  const size = radius * 2;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const guestId = parseInt(e.dataTransfer.getData('guestId'), 10);
    if (!isNaN(guestId)) {
      onSeatDrop(seat.globalSeatNumber, guestId);
    }
  }, [seat.globalSeatNumber, onSeatDrop]);

  const handleClick = useCallback(() => {
    onSeatClick(seat.globalSeatNumber);
  }, [seat.globalSeatNumber, onSeatClick]);

  // Seats are draggable too — drag an empty seat onto a guest to assign
  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData('seatNumber', String(seat.globalSeatNumber));
    e.dataTransfer.effectAllowed = 'move';
  }, [seat.globalSeatNumber]);

  return (
    <div
      draggable={!isAssigned}
      onDragStart={!isAssigned ? handleDragStart : undefined}
      className={`
        flex items-center justify-center rounded-full cursor-pointer
        transition-all duration-150 ease-out select-none
        ${isAssigned
          ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md hover:shadow-lg hover:scale-105'
          : 'bg-gradient-to-br from-blue-50 to-blue-200 text-blue-400 shadow-sm hover:shadow-md hover:scale-105 border border-blue-200 cursor-grab active:cursor-grabbing'
        }
        ${isSelected
          ? 'ring-2 ring-sky-400 ring-offset-2 scale-110 z-10'
          : ''
        }
      `}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(radius * 0.7, 9),
        lineHeight: 1,
      }}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      title={isAssigned ? `${seat.assignedGuestName} (${seat.label})` : `${seat.label} — empty (drag to a guest to assign)`}
    >
      {isAssigned
        ? (seat.assignedGuestName?.charAt(0).toUpperCase() || '?')
        : ''
      }
    </div>
  );
};

export default SeatChip;
